import React, { useState, useCallback, useMemo } from "react";

// Utils
import areObjectsEqual from "../../../../utils/areObjectsEqual";

// Constants
const MAX_NAME_LENGTH = 25;
const MAX_DISPLAY_ORDER = 9999;

const INITIAL_ERRORS = {
  approvedName: false,
  displayOrder: false,
  checkName: false,
};

// API Service
const approvedNameAPI = {
  checkNameUnique: async (name, id, artPackId) => {
    const encodedName = encodeURIComponent(name);
    const res = await fetch(`/api/approvedname/checkname/${encodedName}/${id}/${artPackId}`);
    if (res.status === 200) {
      const data = await res.json();
      return data.checkStatus;
    }
    return false;
  },
};


const Form = ({ handleSubmit, formData, onDiscard, typeCheck, artPackId }) => {
  const [form, setForm] = useState(formData);
  const [errors, setErrors] = useState(INITIAL_ERRORS);

  // Check for unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    return !areObjectsEqual(formData, form);
  }, [formData, form]);

  // Check if approved name is unique
  const checkNameUnique = useCallback(async (name, id) => {
    const isNameTaken = await approvedNameAPI.checkNameUnique(name, id, artPackId);
    setErrors(prev => ({ ...prev, checkName: isNameTaken }));
    return isNameTaken;
  }, [artPackId]);

  // Handle input changes
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;

    // Convert display_order to integer
    const processedValue = name === "display_order" ? parseInt(value, 10) || 0 : value;

    setForm(prev => ({ ...prev, [name]: processedValue }));
  }, []);

  // Validate form data
  const validateForm = useCallback(() => {
    const newErrors = {
      approvedName: !form.approved_name,
      displayOrder: !form.display_order || form.display_order <= 0,
      checkName: false,
    };

    setErrors(newErrors);
    return !newErrors.approvedName && !newErrors.displayOrder;
  }, [form]);

  // Handle form submission
  const handleFormSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check if name is unique
    const id = typeCheck === 'updateItem' ? form.id : 0;
    const isNameTaken = await checkNameUnique(form.approved_name, id);
    if (isNameTaken) {
      return;
    }

    // Submit form
    handleSubmit(form);
    onDiscard();
  }, [validateForm, typeCheck, form, checkNameUnique, handleSubmit, onDiscard]);

  // Determine if save button should be disabled
  const isSaveDisabled = useMemo(() => {
    return !hasUnsavedChanges;
  }, [hasUnsavedChanges]);


  return (
    <>
      {/* Approved Name Input */}
      <div className="mt-4">
        <label htmlFor="approved_name" className="form-labels">
          Approved Name:
        </label>
        <input
          type="text"
          name="approved_name"
          id="approved_name"
          onChange={handleChange}
          value={form.approved_name}
          className="form-control inputs"
          maxLength={MAX_NAME_LENGTH}
        />
        {errors.approvedName && (
          <span className="form-text text-danger">
            Approved Name is required
          </span>
        )}
        {errors.checkName && (
          <span className="form-text text-danger">
            This Approved Name already exists, please use a different one.
          </span>
        )}
      </div>

      {/* Display Order Input */}
      <div className="mt-5">
        <label htmlFor="display_order" className="form-labels">
          Display Order:
        </label>
        <input
          type="number"
          name="display_order"
          id="display_order"
          onChange={handleChange}
          value={form.display_order}
          className="form-control inputs"
          min={1}
          max={MAX_DISPLAY_ORDER}
        />
        {errors.displayOrder && (
          <span className="form-text text-danger">
            Display Order is required
          </span>
        )}
      </div>

      {/* Action Buttons */}
      <div className="pt-5">
        <div className="btn btn-secondary form-btn me-4" onClick={onDiscard}>
          Discard
        </div>
        <input
          type="submit"
          value="Save Changes"
          className="btn bg-orange form-btn"
          onClick={handleFormSubmit}
          disabled={isSaveDisabled}
        />
      </div>
    </>
  );
};

export default Form;
